import { Link, Lock } from 'lucide-react'
import type { ComponentType } from '@/types/schemas/workspace'
import type { LinkMode } from '../types'
import { isColorLinkMode } from '../types'
import { setMilestone } from '@/services/analytics'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu'

// ─── Color map ───

const COLOR_MAP: Record<string, string> = {
  purple: 'hsl(var(--link-purple))',
  blue: 'hsl(var(--link-blue))',
  green: 'hsl(var(--link-green))',
  yellow: 'hsl(var(--link-yellow))',
}

const LINK_MODE_OPTIONS: { value: LinkMode; label: string }[] = [
  { value: 'onChange', label: 'On Change' },
]

const COLOR_OPTIONS: { value: LinkMode; label: string }[] = [
  { value: 'blue', label: 'Blue Group' },
  { value: 'green', label: 'Green Group' },
  { value: 'yellow', label: 'Yellow Group' },
  { value: 'purple', label: 'Purple Group' },
]

// ─── Props ───

interface PanelLinkMenuProps {
  linkMode: LinkMode
  componentType: ComponentType
  onLinkModeChange: (mode: LinkMode) => void
}

// ─── Component ───

export function PanelLinkMenu({ linkMode, onLinkModeChange }: PanelLinkMenuProps) {
  const isNone = linkMode === 'none'
  const isColor = isColorLinkMode(linkMode)
  const IconComp = isNone ? Lock : Link
  const iconColor = isNone ? 'hsl(var(--link-none))' : isColor ? COLOR_MAP[linkMode] : undefined

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          title="Link"
          onMouseDown={(e) => e.stopPropagation()}
          className="flex h-6 w-6 items-center justify-center rounded border-none bg-transparent text-muted-foreground outline-hidden ring-0 focus:outline-hidden focus-visible:ring-0 hover:bg-hover hover:text-accent-foreground"
          style={{ padding: 0, cursor: 'pointer', color: iconColor, flexShrink: 0 }}
        >
          <IconComp size={15} strokeWidth={1.8} />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" onMouseDown={(e) => e.stopPropagation()}>
        <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Link Mode
        </DropdownMenuLabel>

        {LINK_MODE_OPTIONS.map((opt) => (
          <DropdownMenuItem
            key={opt.value}
            onSelect={() => onLinkModeChange(opt.value)}
            className={linkMode === opt.value ? 'bg-accent' : ''}
          >
            <Link size={14} strokeWidth={1.8} className="text-muted-foreground" />
            {opt.label}
          </DropdownMenuItem>
        ))}

        <DropdownMenuItem
          onSelect={() => onLinkModeChange('none')}
          className={linkMode === 'none' ? 'bg-accent' : ''}
        >
          <Lock size={14} strokeWidth={1.8} style={{ color: 'hsl(var(--link-none))' }} />
          Lock
        </DropdownMenuItem>

        <DropdownMenuSeparator style={{ backgroundColor: 'hsl(var(--border-strong))' }} />

        {COLOR_OPTIONS.map((opt) => (
          <DropdownMenuItem
            key={opt.value}
            onSelect={() => {
              setMilestone('first_color_link_used_at')
              onLinkModeChange(opt.value)
            }}
            className={linkMode === opt.value ? 'bg-accent' : ''}
          >
            <Link size={14} strokeWidth={1.8} style={{ color: COLOR_MAP[opt.value] }} />
            {opt.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
